import type { ApiError, AuthenticationResult } from './types';
import { clearTokens, readAccessToken, readRefreshToken, writeTokens } from './storage';

export const API_BASE_URL: string =
  (import.meta.env.VITE_ADMIN_API_URL as string | undefined) ?? 'http://localhost:3001/api/v1';

/**
 * The network never reached the API.
 *
 * A separate type from `ApiRequestError` because there is no status, no error code and no correlation id
 * to show — and because the browser's own message is English written for a developer. R0039 fixed exactly
 * this on all three clients ("Failed to fetch" in a browser, a TLS policy string on Android) and the back
 * office was missed: an operator was shown `Failed to fetch` and reasonably read it as bad credentials.
 *
 * The technical cause is kept for the console, never for the screen.
 */
export class ApiNetworkError extends Error {
  public readonly cause: unknown;

  public constructor(cause: unknown) {
    super('Server neodpovedá. Skontrolujte, či beží admin API, a skúste to znova.');
    this.cause = cause;
  }
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly errorCode: string;
  public readonly correlationId: string;

  public constructor(error: ApiError) {
    super(error.message);
    this.status = error.statusCode;
    this.errorCode = error.errorCode;
    this.correlationId = error.correlationId;
  }
}

let refreshInFlight: Promise<boolean> | null = null;

const parseError = async (response: Response): Promise<ApiRequestError> => {
  try {
    const body = (await response.json()) as ApiError;

    return new ApiRequestError(body);
  } catch {
    return new ApiRequestError({
      statusCode: response.status,
      // `statusText` is the server's English reason phrase; it is not copy for an operator. Falling back
      // to Slovak keeps BR-28 true even on a response the API did not shape.
      message: 'Požiadavka zlyhala.',
      errorCode: 'UNKNOWN',
      correlationId: '',
    });
  }
};

const attemptRefresh = async (): Promise<boolean> => {
  const refreshToken = readRefreshToken();

  if (!refreshToken) {
    return false;
  }

  if (!refreshInFlight) {
    refreshInFlight = (async (): Promise<boolean> => {
      try {
        let response: Response;

        try {
          response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          });
        } catch {
          // A refresh that could not leave the machine says nothing about the token. Clearing it here
          // would sign an operator out for a dropped connection, so the tokens are left alone and the
          // caller reports the network failure.
          return false;
        }

        if (!response.ok) {
          clearTokens();

          return false;
        }

        const result = (await response.json()) as AuthenticationResult;

        writeTokens(result.tokens.accessToken, result.tokens.refreshToken);

        return true;
      } finally {
        refreshInFlight = null;
      }
    })();
  }

  return refreshInFlight;
};

export const request = async <TResult>(
  path: string,
  init: RequestInit = {},
  retryOnUnauthorized = true,
): Promise<TResult> => {
  const accessToken = readAccessToken();
  const headers = new Headers(init.headers);

  headers.set('content-type', 'application/json');
  headers.set('accept-language', 'sk');

  if (accessToken) {
    headers.set('authorization', `Bearer ${accessToken}`);
  }

  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  } catch (cause) {
    // eslint-disable-next-line no-console
    console.warn(`[api] Network failure on ${path}:`, cause);

    throw new ApiNetworkError(cause);
  }

  if (response.status === 401 && retryOnUnauthorized) {
    const refreshed = await attemptRefresh();

    if (refreshed) {
      return request<TResult>(path, init, false);
    }
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) {
    return undefined as TResult;
  }

  return (await response.json()) as TResult;
};

export const get = <TResult>(path: string): Promise<TResult> => request<TResult>(path);

export const post = <TResult>(path: string, body: unknown): Promise<TResult> =>
  request<TResult>(path, { method: 'POST', body: JSON.stringify(body) });

export const patch = <TResult>(path: string, body: unknown): Promise<TResult> =>
  request<TResult>(path, { method: 'PATCH', body: JSON.stringify(body) });

export const put = <TResult>(path: string, body: unknown): Promise<TResult> =>
  request<TResult>(path, { method: 'PUT', body: JSON.stringify(body) });

/** Named `del` because `delete` is a reserved word. The first back-office verb that removes anything. */
export const del = <TResult>(path: string): Promise<TResult> => request<TResult>(path, { method: 'DELETE' });
